<template>
  <div id="audit" class="container-fluid py-3">
    <div v-if="!$store.state.userProfile.superAdmin" class="alert alert-warning">Admins only.</div>
    <div v-else>
      <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
        <div>
          <h2 class="mb-0">Billing audit</h2>
          <div class="text-muted small" v-if="report">
            Last run {{ fmtDateTime(report.runAt) }} ·
            <span :class="report.enforce ? 'text-success' : 'text-warning'"><strong>{{ report.enforce ? 'Enforcing' : 'Dry run' }}</strong></span>
            · {{ report.total }} trail systems · {{ report.changed }} changed · {{ report.emailsSent }} emails sent
          </div>
        </div>
        <div class="mt-2">
          <b-btn size="sm" variant="outline-primary" :disabled="busy" @click="run('runAudit')"><i class="fas" :class="busy ? 'fa-sync fa-spin' : 'fa-play'"></i> Run audit now</b-btn>
          <b-btn size="sm" :variant="enforce ? 'outline-danger' : 'success'" class="ml-1" :disabled="busy" @click="confirmEnforce = true">
            {{ enforce ? 'Stop enforcing' : 'Start enforcing' }}
          </b-btn>
          <b-btn v-if="enforce" size="sm" :variant="pauseLegacy ? 'outline-danger' : 'outline-secondary'" class="ml-1" :disabled="busy" @click="confirmLegacy = true">
            {{ pauseLegacy ? 'Stop pausing legacy' : 'Pause legacy systems' }}
          </b-btn>
        </div>
      </div>

      <b-alert :show="!enforce" variant="warning">
        <strong>Dry run.</strong> The engine evaluates every trail system daily and reports here, but does not change any trail system, pause anything, or send email until enforcement is on. Review the "Would change" column, then click <em>Start enforcing</em>.
      </b-alert>
      <b-alert :show="enforce && !pauseLegacy" variant="info">
        <strong>Legacy systems are protected.</strong> Trail systems that predate the engine and never paid are marked <em>Unpaid (legacy)</em>: reported, but not paused, downgraded or emailed. Ramp them one at a time with <em>Extend trial</em> (starts a dated trial with the normal warning emails) or <em>Mark complimentary</em>. Turn on <em>Pause legacy systems</em> only when you want them all paused at once (still without email).
      </b-alert>
      <b-alert :show="enforce" variant="light" class="small">
        First-time evaluations never send retrospective email: active customers don't get "you're all set" and long-cancelled accounts don't get "subscription ended". Only forward-looking mail goes out (trial ending, payment failing now).
      </b-alert>
      <b-alert :show="!!error" variant="danger" dismissible @dismissed="error = ''">{{ error }}</b-alert>

      <div v-if="report" class="mb-3">
        <b-badge v-for="(n, status) in report.counts" :key="status" pill class="mr-1 clickable" :variant="filterStatus === status ? 'dark' : variantFor(status)" @click="filterStatus = filterStatus === status ? '' : status">
          {{ labelFor(status) }} {{ n }}
        </b-badge>
        <b-badge pill class="mr-1 clickable" :variant="filterStatus === 'overLimit' ? 'dark' : 'secondary'" @click="filterStatus = filterStatus === 'overLimit' ? '' : 'overLimit'">Over limit {{ report.overLimit }}</b-badge>
        <b-badge pill class="mr-1 clickable" :variant="filterStatus === 'changed' ? 'dark' : 'secondary'" @click="filterStatus = filterStatus === 'changed' ? '' : 'changed'">Would change {{ report.changed }}</b-badge>
        <b-form-input v-model="search" size="sm" placeholder="Search name, owner, email…" class="d-inline-block ml-2" style="width: 260px"></b-form-input>
      </div>

      <!-- Trial funnel: who is trying TrailHUB right now and whether they need a call -->
      <b-card no-body class="mb-3">
        <b-card-header class="d-flex flex-wrap align-items-center justify-content-between py-2">
          <strong><i class="fas fa-seedling mr-1 text-info"></i> Free trials</strong>
          <span class="small text-muted">Welcome email on day 1 (copy to you) · check-in on day 10 if no trails · reminders 7 and 1 days before · "we saved your work" a week after it lapses</span>
        </b-card-header>
        <b-table :items="trials" :fields="trialFields" small hover responsive show-empty empty-text="No current or recently lapsed trials." class="mb-0">
          <template #cell(name)="d">
            <a :href="'/ts/' + d.item.id" target="_blank">{{ d.item.name || d.item.id }}</a>
            <b-badge v-if="d.item.status === 'paused'" variant="secondary" class="ml-1">lapsed</b-badge>
            <div class="small text-muted" v-if="d.item.owner">{{ d.item.owner.name }}<span v-if="d.item.owner.organization"> · {{ d.item.owner.organization }}</span> · <a :href="mailtoFor(d.item)">{{ d.item.owner.email }}</a></div>
          </template>
          <template #cell(timeline)="d">
            <div class="small">
              Started {{ fmtDate(d.item.createdOn) }}<br>
              <span :class="d.item.status === 'paused' ? 'text-muted' : daysLeft(d.item) <= 7 ? 'text-danger' : ''">{{ d.item.status === 'paused' ? 'Ended' : 'Ends' }} {{ fmtDate(d.item.trialEndsAt) }}<span v-if="d.item.status !== 'paused'"> ({{ daysLeft(d.item) }} days)</span></span>
            </div>
          </template>
          <template #cell(activity)="d">
            <div class="small">
              <span :class="d.item.totalTrails ? 'text-success' : 'text-danger'"><strong>{{ d.item.totalTrails }}</strong> trails<span v-if="d.item.totalDistance"> · {{ (d.item.totalDistance / 1609.34).toFixed(1) }} mi</span></span><br>
              Last edit {{ ago(d.item.updatedOn) }} · last sign-in {{ ago(d.item.lastSignIn) }}
            </div>
          </template>
          <template #cell(next)="d">
            <span class="small" :class="'text-' + nextStep(d.item).variant">{{ nextStep(d.item).text }}</span>
          </template>
          <template #cell(actions)="d">
            <b-btn size="sm" variant="outline-primary" class="mr-1" :disabled="busy" @click="run('extendTrial', { trailSystemId: d.item.id, days: 14 })">+14 days</b-btn>
            <b-btn size="sm" variant="outline-secondary" :href="mailtoFor(d.item)"><i class="fas fa-envelope"></i></b-btn>
          </template>
        </b-table>
      </b-card>

      <p v-if="!report" class="text-muted">No report yet. Click <em>Run audit now</em>.</p>
      <b-table v-else :items="rows" :fields="fields" small hover responsive sort-by="status" class="bg-white">
        <template #cell(name)="d">
          <a :href="'/ts/' + d.item.id" target="_blank">{{ d.item.name || d.item.id }}</a>
          <div class="small text-muted" v-if="d.item.owner">{{ d.item.owner.name }} · {{ d.item.owner.email }}<span v-if="d.item.owner.organization"> · {{ d.item.owner.organization }}</span></div>
        </template>
        <template #cell(status)="d">
          <b-badge :variant="variantFor(d.item.status)">{{ labelFor(d.item.status) }}</b-badge>
          <span v-if="d.item.tier" class="small ml-1">{{ d.item.tier }}</span>
          <div class="small text-muted">{{ d.item.reason }}<span v-if="d.item.previousStatus && d.item.previousStatus !== d.item.status"> (was {{ d.item.previousStatus }})</span></div>
        </template>
        <template #cell(dates)="d">
          <div class="small">
            <div v-if="d.item.status === 'trial'">Trial ends {{ fmtDate(d.item.trialEndsAt) }}</div>
            <div v-if="d.item.status === 'grace'">Pauses {{ fmtDate(d.item.graceEndsAt) }}</div>
            <div v-if="d.item.status === 'seasonal'">{{ d.item.reason === 'scheduled_start' ? 'Starts' : 'Resumes' }} {{ fmtDate(d.item.resumesAt) }}</div>
            <div v-if="d.item.price">{{ d.item.price }}</div>
            <div v-if="d.item.renewsAt">Renews {{ fmtDate(d.item.renewsAt) }}</div>
            <div v-if="d.item.cancelAt" class="text-danger">Cancels {{ fmtDate(d.item.cancelAt) }}</div>
            <div v-if="d.item.subscriptionStatus">Stripe: {{ d.item.subscriptionStatus }}</div>
          </div>
        </template>
        <template #cell(flags)="d">
          <b-badge v-if="d.item.overLimit" variant="warning">over limit</b-badge>
          <b-badge v-if="d.item.changed" variant="info">{{ enforce ? 'changed' : 'would change' }}</b-badge>
          <div class="small text-muted" v-if="d.item.emailsDue && d.item.emailsDue.length">mail: {{ d.item.emailsDue.join(', ') }}</div>
        </template>
        <template #cell(actions)="d">
          <b-dropdown size="sm" variant="link" text="Actions" right no-caret toggle-class="p-0">
            <template #button-content><i class="fas fa-ellipsis-h"></i></template>
            <b-dropdown-item @click="run('extendTrial', { trailSystemId: d.item.id, days: 14 })">Extend trial 14 days</b-dropdown-item>
            <b-dropdown-item @click="run('extendTrial', { trailSystemId: d.item.id, days: 30 })">Extend trial 30 days</b-dropdown-item>
            <b-dropdown-item @click="run('extendTrial', { trailSystemId: d.item.id, days: 90 })">Extend trial 90 days</b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item v-if="d.item.status !== 'override'" @click="run('setOverride', { trailSystemId: d.item.id, override: true })">Mark complimentary (override)</b-dropdown-item>
            <b-dropdown-item v-else @click="run('setOverride', { trailSystemId: d.item.id, override: false })">Remove override</b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item @click="run('reevaluate', { trailSystemId: d.item.id })">Re-evaluate now</b-dropdown-item>
            <b-dropdown-item :href="'/admin'" target="_blank">Open in admin</b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
    </div>

    <Confirm
      v-if="confirmLegacy"
      :text="pauseLegacy ? 'Stop pausing legacy systems? Already-paused systems stay paused until re-evaluated with a trial or plan.' : 'Pause every legacy unpaid trail system now? Their public maps go dark and stale tiers are removed. No email is sent. This affects every system marked Unpaid (legacy).'"
      v-on:confirmed="toggleLegacy($event)"
    />
    <Confirm
      v-if="confirmEnforce"
      :text="enforce ? 'Stop enforcing? The engine will keep reporting but stop pausing systems and sending email.' : 'Start enforcing? From now on the engine will pause trail systems whose trial or payment lapsed, downgrade tiers, and email owners. Check the dry-run report first.'"
      v-on:confirmed="toggleEnforce($event)"
    />
  </div>
</template>

<script>
import firebase from 'firebase'
import Confirm from '../Confirm'
import { fmtDate } from '../../assets/lib/billingStatus'
const fb = require('../../firebaseConfig.js')

const LABELS = { trial: 'Trial', active: 'Active', grace: 'Payment failed', paused: 'Paused', override: 'Complimentary', legacy: 'Unpaid (legacy)', seasonal: 'Seasonal pause' }
const VARIANTS = { trial: 'info', active: 'success', grace: 'danger', paused: 'secondary', override: 'primary', legacy: 'warning', seasonal: 'info' }

export default {
  components: { Confirm },
  data () {
    return {
      report: null,
      enforce: false,
      pauseLegacy: false,
      confirmLegacy: false,
      busy: false,
      error: '',
      filterStatus: '',
      search: '',
      confirmEnforce: false,
      unsubscribe: [],
      trials: [],
      trialFields: [
        { key: 'name', label: 'Trial / owner' },
        { key: 'timeline', label: 'Timeline' },
        { key: 'activity', label: 'Activity' },
        { key: 'next', label: 'Suggested next step' },
        { key: 'actions', label: '' }
      ],
      fields: [
        { key: 'name', label: 'Trail system / owner', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'dates', label: 'Dates' },
        { key: 'flags', label: '' },
        { key: 'actions', label: '' }
      ]
    }
  },
  computed: {
    rows () {
      if (!this.report) return []
      const q = this.search.trim().toLowerCase()
      return this.report.rows.filter(r => {
        if (this.filterStatus === 'overLimit') { if (!r.overLimit) return false } else if (this.filterStatus === 'changed') { if (!r.changed) return false } else if (this.filterStatus && r.status !== this.filterStatus) return false
        if (!q) return true
        const hay = [r.name, r.id, r.owner && r.owner.name, r.owner && r.owner.email, r.owner && r.owner.organization, r.tier, r.reason].join(' ').toLowerCase()
        return hay.includes(q)
      })
    }
  },
  mounted () {
    this.loadTrials()
    this.unsubscribe.push(fb.db.collection('billingReports').doc('latest').onSnapshot(snap => { this.report = snap.exists ? snap.data() : null }, err => { this.error = err.message }))
    this.unsubscribe.push(fb.db.collection('config').doc('billing').onSnapshot(snap => {
      const c = snap.exists ? snap.data() : {}
      this.enforce = c.enforce === true
      this.pauseLegacy = c.pauseLegacy === true
    }, err => { console.log(err.message) }))
  },
  beforeDestroy () { this.unsubscribe.forEach(fn => fn()) },
  methods: {
    fmtDate,
    fmtDateTime (v) { const d = v && typeof v.toDate === 'function' ? v.toDate() : new Date(v); return d.toLocaleString() },
    labelFor (s) { return LABELS[s] || s },
    daysLeft (t) { return Math.ceil((new Date(t.trialEndsAt) - Date.now()) / 86400000) },
    ago (v) {
      if (!v) return 'never'
      const days = Math.floor((Date.now() - new Date(v)) / 86400000)
      return days <= 0 ? 'today' : days === 1 ? 'yesterday' : days + ' days ago'
    },
    // One-line coaching hint per trial.
    nextStep (t) {
      const left = this.daysLeft(t)
      if (t.status === 'paused') return { text: 'Lapsed without a plan. Worth one personal note.', variant: 'muted' }
      if (!t.totalTrails && this.ago(t.updatedOn) !== 'today' && left < 25) return { text: 'No trails yet and no activity. Offer a walkthrough call.', variant: 'danger' }
      if (!t.totalTrails) return { text: 'Just started. Watch for trails in the next few days.', variant: 'info' }
      if (left <= 7) return { text: 'Active and ending soon. Ask what they need to decide.', variant: 'warning' }
      return { text: 'Building their map. Check in once they have a few trails.', variant: 'success' }
    },
    mailtoFor (t) {
      if (!t.owner || !t.owner.email) return '#'
      const first = (t.owner.name || '').split(' ')[0]
      const body = t.totalTrails
        ? `Hi ${first},\n\nI saw you've started building ${t.name} on TrailHUB. If anything is confusing, or you'd like me to look over the map with you, just reply with a couple of times that work.\n\nNick`
        : `Hi ${first},\n\nI saw you created ${t.name} on TrailHUB but haven't added trails yet. That's usually the hardest step. Happy to do a quick 20-minute screen share and get them on the map with you. Reply with a couple of times that work.\n\nNick`
      return `mailto:${t.owner.email}?subject=${encodeURIComponent('TrailHUB: ' + t.name)}&body=${encodeURIComponent(body)}`
    },
    async loadTrials () {
      try {
        const fn = firebase.app().functions('us-central1').httpsCallable('adminBilling')
        const { data } = await fn({ action: 'trialActivity' })
        this.trials = data
      } catch (err) {
        console.log('trialActivity', err.message)
      }
    },
    variantFor (s) { return VARIANTS[s] || 'secondary' },
    async run (action, payload = {}) {
      this.busy = true
      this.error = ''
      try {
        const fn = firebase.app().functions('us-central1').httpsCallable('adminBilling')
        await fn({ action, payload })
        if (action !== 'runAudit') await fn({ action: 'runAudit' })
        this.loadTrials()
      } catch (err) {
        this.error = err.message
      }
      this.busy = false
    },
    async toggleEnforce (confirmed) {
      this.confirmEnforce = false
      if (!confirmed) return
      await this.run('setEnforce', { enforce: !this.enforce })
    },
    async toggleLegacy (confirmed) {
      this.confirmLegacy = false
      if (!confirmed) return
      await this.run('setEnforce', { pauseLegacy: !this.pauseLegacy })
    }
  }
}
</script>

<style scoped>
.clickable { cursor: pointer; }
</style>
